// react
import { useCallback, useMemo, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Box, Chip, Grid, Stack, Typography } from '@mui/material';

// icons
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import IconPdf from '@mui/icons-material/PictureAsPdfOutlined';

// types
import { Invoice } from '../../../@types';

// dialogs
import {
	DialogDocumentViewer,
	ViewerDocumentInfo,
} from '../../dialogs/DialogDocumentViewer/DialogDocumentViewer';

// cards
import { CardDetail } from '../CardDetail/CardDetail';

// services
import { fetchAiDocuments } from '../../../services/aiApi';

// data
import { ContractDocument } from '../../../data';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dates';

export interface CardInvoiceDetailsProps {
	invoice: Invoice;
}

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
	DRAFT: 'default',
	PENDING_REVIEW: 'info',
	READY_FOR_PAYMENT: 'warning',
	APPROVED: 'success',
	REJECTED: 'error',
};

// Distinct accent for the invoice card — a rose that none of the other
// CardDetail variants use (amber=workPlan, teal=condition, purple=payment/
// activity, green=financial, blue=contract, indigo). Drives the left border
// and the header icon so invoices are visually recognizable at a glance.
const INVOICE_ACCENT = '#db2777';

const sizeLabel = (bytes: number): string =>
	bytes < 1024 * 1024
		? `${Math.round(bytes / 1024)} KB`
		: `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const supplierName = (invoice: Invoice): string =>
	typeof invoice.supplier === 'object' && invoice.supplier ? invoice.supplier.name : '';

/**
 * Compact, reusable summary of a single invoice record (the same
 * `public.invoice` row rendered anywhere a milestone/condition or voucher is
 * shown). Attachments open in the shared DialogDocumentViewer — the card owns
 * that dialog so host views only need to render `<CardInvoiceDetails invoice />`.
 */
export const CardInvoiceDetails = ({ invoice }: CardInvoiceDetailsProps) => {
	const { t } = useTranslation();

	const [viewerOpen, setViewerOpen] = useState(false);
	const [viewerIndex, setViewerIndex] = useState(0);
	const [viewerDocInfos, setViewerDocInfos] = useState<ViewerDocumentInfo[]>([]);

	const attachments = invoice.attachments || [];

	const viewerDocuments: ContractDocument[] = useMemo(
		() =>
			attachments.map((att) => ({
				id: String(att.id),
				name: att.name,
				size: sizeLabel(att.size),
				type: (att.name.split('.').pop()?.toLowerCase() || 'doc') as
					| 'doc'
					| 'pdf'
					| 'image'
					| 'xlsx',
				category: 'other' as const,
				uploadedAt: att.uploadedAt,
				uploadedBy: att.uploadedBy,
			})),
		[attachments]
	);

	const viewerBlobFilenames = useMemo(() => attachments.map((att) => att.name), [attachments]);

	const handleViewAttachment = useCallback(
		async (index: number) => {
			const att = attachments[index];
			if (!att) return;
			setViewerIndex(index);
			setViewerOpen(true);

			const initialInfos: ViewerDocumentInfo[] = attachments.map((a) => ({
				name: a.name,
				size: sizeLabel(a.size),
				uploadedAt: a.uploadedAt,
				uploadedBy: a.uploadedBy,
				sourceType: 'invoice',
				sourceLabel: supplierName(invoice),
			}));
			setViewerDocInfos(initialInfos);

			try {
				const res = await fetchAiDocuments({ search: att.name });
				const aiDoc =
					res.data?.find((d: { filename?: string }) => d.filename === att.name) || res.data?.[0];
				if (aiDoc?.job_id) {
					setViewerDocInfos((prev) => {
						const updated = [...prev];
						if (updated[index]) {
							updated[index] = {
								...updated[index],
								id: aiDoc.id,
								jobId: aiDoc.job_id || undefined,
							};
						}
						return updated;
					});
				}
			} catch {
				/* silent — viewer still opens with the blob filename */
			}
		},
		[attachments, invoice]
	);

	const amountLabel =
		invoice.amount != null
			? formatCurrency({ currency: invoice.currency, value: invoice.amount })
			: '';
	const taxLabel =
		invoice.tax != null && invoice.tax > 0
			? formatCurrency({ currency: invoice.currency, value: invoice.tax })
			: null;
	const dateLabel = invoice.date ? formatDate({ date: invoice.date }) : null;
	const dateDueLabel = invoice.dateDue ? formatDate({ date: invoice.dateDue }) : null;

	const status = (invoice.status || '').toUpperCase();
	const statusColor = STATUS_COLOR[status] || 'default';

	const details: { label: string; value: string }[] = [];
	if (amountLabel) details.push({ label: t('amount', 'Amount'), value: amountLabel });
	if (taxLabel) details.push({ label: t('tax', 'Tax'), value: taxLabel });
	if (dateLabel)
		details.push({ label: t('invoices.invoiceDate', 'Invoice Date'), value: dateLabel });
	if (dateDueLabel) details.push({ label: t('invoices.dateDue', 'Date Due'), value: dateDueLabel });

	return (
		<>
			<CardDetail
				icon={ReceiptLongIcon}
				title={`${t('invoice', 'Invoice')}${invoice.num ? ` · ${invoice.num}` : ''}`}
				variant="payment"
				themeColor={INVOICE_ACCENT}
				iconColor={INVOICE_ACCENT}
				contentPadding
				headerActions={
					status ? (
						<Chip
							label={status === 'PENDING_REVIEW' ? 'PENDING' : status.replace(/_/g, ' ')}
							size="small"
							color={statusColor}
							variant="outlined"
						/>
					) : undefined
				}>
				{details.length > 0 && (
					<Grid
						container
						spacing={1.5}
						sx={{ mb: attachments.length || invoice.description ? 1.5 : 0 }}>
						{details.map((d) => (
							<Grid item xs={6} sm={3} key={d.label}>
								<Typography variant="caption" color="text.secondary" display="block">
									{d.label}
								</Typography>
								<Typography variant="body2" fontWeight={600}>
									{d.value}
								</Typography>
							</Grid>
						))}
					</Grid>
				)}

				{invoice.description && (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mb: attachments.length ? 1.5 : 0 }}>
						{invoice.description}
					</Typography>
				)}

				{attachments.length > 0 && (
					<Stack spacing={1}>
						{attachments.map((att, idx) => (
							<Box
								key={att.id}
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									p: 1,
									bgcolor: 'action.hover',
									borderRadius: 1,
								}}>
								<IconPdf sx={{ fontSize: 20, color: 'error.main' }} />
								<Typography
									variant="body2"
									noWrap
									onClick={() => handleViewAttachment(idx)}
									sx={{
										flex: 1,
										minWidth: 0,
										fontWeight: 500,
										cursor: 'pointer',
										color: 'primary.main',
										'&:hover': { textDecoration: 'underline' },
									}}>
									{att.name}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{sizeLabel(att.size)}
								</Typography>
							</Box>
						))}
					</Stack>
				)}
			</CardDetail>

			{viewerDocuments.length > 0 && (
				<DialogDocumentViewer
					isOpen={viewerOpen}
					currentIndex={viewerIndex}
					documents={viewerDocuments}
					blobFilenames={viewerBlobFilenames}
					documentInfos={viewerDocInfos}
					onClose={() => setViewerOpen(false)}
					onNavigate={setViewerIndex}
				/>
			)}
		</>
	);
};
